import { Feature } from 'ol';
import { MapLayerEnum } from './enum';
import Point from 'ol/geom/Point.js';
import LineString from 'ol/geom/LineString';
import { Map as OlMap } from 'ol';
import LayerVector from 'ol/layer/Vector.js';
import SourceVector from 'ol/source/Vector';
import { Coordinate } from 'ol/coordinate';
import { fromLonLat } from 'ol/proj';

class MapBaseOperate {
  map: any;
  sourceMap: { [key: string]: any };
  constructor(mapInstance: OlMap) {
    this.map = mapInstance;
    this.sourceMap = {};
  }

  /**
   * 统一坐标转换适配器
   * 无论外部传入什么，这里负责转成当前地图 View 需要的投影坐标
   * 默认约定：外部调用的入参永远是经纬度 [lon, lat]
   * @protected
   * @param {number[]} coordinate [lon, lat]
   * @return {*}  {number[]} 投影后的坐标
   * @memberof MapBaseOperate
   */
  protected transformCoordinate(coordinate: number[]): number[] {
    if (!coordinate || coordinate.length < 2) return coordinate;

    // 保证坐标格式为number
    const safeCoordinate = [Number(coordinate[0]), Number(coordinate[1])];

    // 动态获取当前地图 View 的投影系
    const view = this.map.getView();
    const viewProjection = view ? view.getProjection() : null;

    // 如果地图是EPSG:3857(平面投影)，则进行转换
    if (viewProjection?.getCode() === 'EPSG:3857') {
      return fromLonLat(safeCoordinate);
    }

    // 否则地图为EPSG:4326(曲面投影)，原样返回
    return safeCoordinate;
  }

  /**
   * 针对线、面等多点情况的批量坐标转换
   * @protected
   * @param {any[]} coordinates number[][] | Coordinate[]
   * @return {*}  {any[]} 投影后的坐标数组
   * @memberof MapBaseOperate
   */
  protected transformCoordinates(coordinates: any[]): any[] {
    if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
      return coordinates.map((c: any) => this.transformCoordinate(c));
    }
    return coordinates;
  }

  /**
   * 添加图层，默认把绘制图层也添加上
   * @param layers 图层数组
   */
  addLayers = (layers: { layer: any; layerId: MapLayerEnum }[]) => {
    layers?.forEach((item: any) => {
      const _layer = item.layer;
      if (_layer) {
        _layer.set('layerId', item.layerId);
        this.map.addLayer(_layer);
      }
    });
  };

  /**
   * 清空指定图层
   */
  clearLayer(layerId: string) {
    const l = this.getLayer(layerId);
    l && l.getSource().clear();
    l && this.map.removeLayer(l);
  }

  /**
   * 获取图层
   * @param {string | undefined | null} layerId 图层Id
   */
  getLayer = (layerId: string | undefined | null) => {
    if (!layerId) {
      return null;
    }
    const layers = this.map.getLayers().getArray();
    for (const l of layers) {
      if (l.values_.layerId === layerId) {
        return l;
      }
    }
    return null;
  };

  /**
   * 更改地图中心点
   * @param coordinates
   * @param r
   */
  changeCenter = (coordinates: number[]) => {
    const viewCoords = this.transformCoordinate(coordinates);
    const curView = this.map.getView();
    curView.animate({
      center: viewCoords,
      duration: 400,
    });
  };

  /**
   * 点的feature
   * @param {Array} coordinates 点的位置
   * @param {any} style 样式
   * @param {string} pointId 点的id
   */
  createPointFeature = (coordinates: any, style: any, pointId: string) => {
    const viewCoords = this.transformCoordinate(coordinates);
    const pointFeature = new Feature({
      geometry: new Point(viewCoords),
    });
    pointFeature.setId(pointId);
    pointFeature.setStyle(style);
    return pointFeature;
  };

  /**
   * 线的feature
   * @param {Coordinate[] | number[]} coordinates 线的位置
   * @param {any} style 样式
   * @param {string} pointId 点的id
   * @returns
   */
  createLineFeature(
    coordinates: Coordinate[] | number[],
    style: any,
    lineId: string
  ) {
    const viewCoords = this.transformCoordinates(coordinates as any[]);
    const line = new LineString(viewCoords);
    const lineFeature = new Feature({ geometry: line });
    lineFeature.setId(lineId);
    lineFeature.setStyle(style);
    return lineFeature;
  }

  /**
   * 创建图层
   * @param opts 图层配置 style zIndex等
   * @param {string} layerId 图层Id
   */
  createLayer = (layerId: string, opts?: any) => {
    const sourceKey = `${layerId}-source`;
    this.sourceMap[sourceKey] = new SourceVector();
    const newLayer = new LayerVector({
      ...opts,
      source: this.sourceMap[sourceKey],
    });
    newLayer.set('layerId', layerId);
    this.map.addLayer(newLayer);
    return newLayer;
  };

  /**
   * 添加要素到图层
   * @param feature 要素的feature
   * @param {string} layerId 图层Id
   */
  addFeature(feature: any, layerId: string) {
    const sourceKey = `${layerId}-source`;
    const source = this.sourceMap[sourceKey];
    source && source.addFeature(feature);
  }

  /**
   * 图层显示与隐藏
   * @param {string} layerId
   * @param {boolean} status
   */
  changeLayerVisibility(layerId: string, status: boolean) {
    const layer = this.getLayer(layerId);
    layer && layer.setVisible(status);
  }

  /**
   * 设置地图缩放级别
   * @param zoom
   */
  setZoom(zoom: number) {
    const view = this.map.getView();
    view.setZoom(zoom);
  }

  /**
   * 通用清除方法：清除单个Feature
   * @param feature 要清除的Feature
   * @param layerEnum Feature所在的图层枚举
   * @returns null（用于将feature引用设为null）
   */
  protected clearFeature(feature: any | null, layerEnum: MapLayerEnum): null {
    if (feature) {
      const source = this.getLayer(layerEnum)?.getSource();
      if (source) {
        source.removeFeature(feature);
      }
    }
    return null;
  }

  /**
   * 通用清除方法：清除Feature数组
   * @param features Feature数组
   * @param layerEnum Feature所在的图层枚举
   * @returns 空数组（用于清空原数组）
   */
  protected clearFeatures(features: any[], layerEnum: MapLayerEnum): any[] {
    if (features && features.length > 0) {
      const source = this.getLayer(layerEnum)?.getSource();
      if (source) {
        features.forEach((feature) => {
          source.removeFeature(feature);
        });
      }
    }
    return [];
  }

  /**
   * 通用清除方法：清除Overlay数组
   * @param overlays Overlay数组
   * @param map 地图实例
   * @returns 空数组（用于清空原数组）
   */
  protected clearOverlays(overlays: any[], map: any): any[] {
    if (overlays && overlays.length > 0) {
      overlays.forEach((overlay) => {
        map.removeOverlay(overlay);
      });
    }
    return [];
  }
}

export default MapBaseOperate;
